import style from '../ErpDialogPositions.module.scss';

import { Box } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { Collapse } from '@work4all/components/lib/components/collapse';

import { EditTable } from '../../../../../containers/mask-overlays/mask-overlay/views/erp/components/tab-panels/positions/components/edit-table/EditTable';
import { NOT_EDITABLE_ROWS } from '../../consts';
import { ErpObject, GroupedPosition } from '../../types';
import { ErpDialogPositionsProps } from '../ErpDialogPositions';
import { ExpandAllBom } from '../ExpandAllBom';

import { useGroupedPositions } from './use-grouped-positions';

interface ErpDialogGroupedPositionsProps
  extends Omit<ErpDialogPositionsProps, 'entry'> {
  positionList: GroupedPosition[];
  supplierId?: string;
  allData: ErpObject[];
  onRefetch: () => void;
}

export const ErpDialogGroupedPositions = (
  props: ErpDialogGroupedPositionsProps
) => {
  const {
    positionList,
    onItemsChanged,
    supplierId,
    groupByType,
    isEditMode,
    allData,
    onRefetch,
    ...collapseProps
  } = props;

  const result = useGroupedPositions({
    positionList,
    onItemsChanged,
    supplierId,
    groupByType,
    isEditMode,
    allData,
    onRefetch,
  });

  const selectedRowIds = useMemo(() => {
    return result.items.reduce((prev, cur) => {
      prev[cur.localId] = true;
      return prev;
    }, {} as Record<string, boolean>);
  }, [result.items]);

  // Keep selection between tabs
  useEffect(() => {
    return result.items.forEach((item) => {
      if (item.selected) {
        result.tableInstanceRef.current.toggleRowSelected(item.localId, true);
        item.selected = undefined;
      } else if (item.selected === false) {
        result.tableInstanceRef.current.toggleRowSelected(item.localId, false);
        item.selected = undefined;
      }
    });
  }, [result.items, result.tableInstanceRef]);

  const [open, setOpen] = useState(false);

  const onCollapsePosition = result.onCollapsePosition;
  useEffect(() => {
    setOpen(collapseProps.maximizedMask);
    positionList.forEach((position) => {
      onCollapsePosition(position, collapseProps.maximizedMask);
    });
  }, [collapseProps.maximizedMask, positionList, onCollapsePosition]);

  return (
    <Collapse
      open={open}
      {...collapseProps}
      onToggled={(open) => {
        setOpen(open);
      }}
      headerBar={
        props.showTableHeader && (
          <Box
            width="100%"
            height="100%"
            alignItems="center"
            display="flex"
            justifyContent="space-between"
            paddingRight="2rem"
          >
            <ExpandAllBom
              onExpandAll={() => {
                collapseProps.onToggled(true);
              }}
              onCloseAll={() => {
                collapseProps.onToggled(false);
              }}
            />
            {result.resetColumnsElement}
          </Box>
        )
      }
    >
      <EditTable<GroupedPosition>
        classes={{
          table: style.table,
          tbody: style.tbody,
        }}
        rowSizeMode={result.erpSize.value}
        showSelectionColumn
        showSelectionColumnOnInit
        hideRelationMarkers
        hideCheckboxOnChildren={isEditMode}
        fitSpace
        editableRows={NOT_EDITABLE_ROWS}
        initialState={{
          selectedRowIds,
        }}
        {...result}
      />
    </Collapse>
  );
};
